import { useState, useEffect, useCallback } from 'react'
import {
  ProductCategories,
  ProductCategoryLabels,
  ProductStatuses,
  ConditionRatings,
  type Product,
  type ProductInput,
  type MasterProduct,
  type SubstanceOfConcern,
  type MaterialComposition,
  type SupplyChainData,
  type EndOfLifeData,
  type SparePartsCatalog,
  type BatteryPassport,
  type DppPassportFields,
} from '@esf/shared'
import {
  SubstancesOfConcernForm,
  MaterialCompositionForm,
  SupplyChainForm,
  EndOfLifeForm,
  SparePartsForm,
  BatteryPassportForm,
  DppPassportForm,
} from './dpp'
import { getApiBase } from '../utils/api'

interface ProductFormPageProps {
  productId?: string | null
  masterProducts?: MasterProduct[]
  onComplete: () => void
  onCancel: () => void
}

type TabId = 'basic' | 'location' | 'manufacturer' | 'sustainability' | 'substances' | 'supply' | 'endoflife' | 'battery' | 'dppPassport' | 'notes'

const defaultFormData: ProductInput = {
  name: '',
  category: 'seating',
  subcategory: '',
  model: '',
  serialNumber: '',
  gtin: '',
  installationSite: '',
  installationDate: '',
  status: 'PENDING_INSTALLATION',
  condition: undefined,
  manufacturer: '',
  manufacturerCountry: '',
  materials: [],
  certifications: [],
  carbonFootprint: undefined,
  recyclabilityScore: undefined,
  expectedLifespan: undefined,
  warrantyEndDate: '',
  repairabilityScore: undefined,
  description: '',
  notes: '',
  tags: [],
}

export default function ProductFormPage({ productId, masterProducts = [], onComplete, onCancel }: ProductFormPageProps) {
  const base = getApiBase()
  const defaultHeaders = { 'Content-Type': 'application/json' }

  const [formData, setFormData] = useState<ProductInput>(defaultFormData)
  const [product, setProduct] = useState<Product | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [materialsInput, setMaterialsInput] = useState('')
  const [certificationsInput, setCertificationsInput] = useState('')
  const [tagsInput, setTagsInput] = useState('')
  const [activeTab, setActiveTab] = useState<TabId>('basic')
  const [isLoading, setIsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [selectedMasterProductId, setSelectedMasterProductId] = useState<string>('')
  const [templateSearch, setTemplateSearch] = useState('')
  const [templateSelectorExpanded, setTemplateSelectorExpanded] = useState(true)

  // DPP-specific state
  const [substancesOfConcern, setSubstancesOfConcern] = useState<SubstanceOfConcern[]>([])
  const [materialComposition, setMaterialComposition] = useState<MaterialComposition | null>(null)
  const [supplyChain, setSupplyChain] = useState<SupplyChainData | null>(null)
  const [endOfLife, setEndOfLife] = useState<EndOfLifeData | null>(null)
  const [sparePartsCatalog, setSparePartsCatalog] = useState<SparePartsCatalog | null>(null)
  const [batteryPassport, setBatteryPassport] = useState<Partial<BatteryPassport> | null>(null)
  const [dppPassportData, setDppPassportData] = useState<Partial<DppPassportFields>>({})

  // Check if product has battery (solar_power category)
  const hasBattery = formData.category === 'solar_power'
  const isEditing = !!productId

  // Get active master products for the selector
  const activeMasterProducts = masterProducts.filter(mp => mp.isActive)

  // Filter templates by search
  const filteredTemplates = activeMasterProducts.filter(mp => {
    if (!templateSearch) return true
    const search = templateSearch.toLowerCase()
    return (
      mp.name.toLowerCase().includes(search) ||
      mp.category.toLowerCase().includes(search) ||
      mp.manufacturer?.toLowerCase().includes(search) ||
      mp.model?.toLowerCase().includes(search) ||
      ProductCategoryLabels[mp.category as keyof typeof ProductCategoryLabels]?.toLowerCase().includes(search)
    )
  })

  // Fetch product if editing
  const fetchProduct = useCallback(async () => {
    if (!productId) return
    setIsFetching(true)
    try {
      const res = await fetch(`${base}/assets/${productId}`, {
        headers: defaultHeaders,
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to fetch product')
      const data = await res.json()
      setProduct(data)
    } catch {
      setErrors({ fetch: 'Failed to load product' })
    } finally {
      setIsFetching(false)
    }
  }, [productId, base])

  useEffect(() => {
    if (productId) {
      fetchProduct()
    }
  }, [productId, fetchProduct])

  // Populate form when product is loaded
  useEffect(() => {
    if (product) {
      // Populate basic form data
      setFormData({
        name: product.name,
        category: product.category,
        subcategory: product.subcategory || '',
        model: product.model || '',
        serialNumber: product.serialNumber || '',
        gtin: product.gtin || '',
        installationSite: product.installationSite || '',
        installationDate: product.installationDate?.split('T')[0] || '',
        status: product.status,
        condition: product.condition,
        manufacturer: product.manufacturer || '',
        manufacturerCountry: product.manufacturerCountry || '',
        materials: product.materials || [],
        certifications: product.certifications || [],
        carbonFootprint: product.carbonFootprint,
        recyclabilityScore: product.recyclabilityScore,
        expectedLifespan: product.expectedLifespan,
        warrantyEndDate: product.warrantyEndDate?.split('T')[0] || '',
        repairabilityScore: product.repairabilityScore,
        description: product.description || '',
        notes: product.notes || '',
        tags: product.tags || [],
      })
      // Handle materials - could be string[] or MaterialComposition[]
      const materialsArray = product.materials || []
      const materialsStrings = materialsArray.map((m: string | { name: string }) =>
        typeof m === 'string' ? m : m.name
      )
      setMaterialsInput(materialsStrings.join(', '))
      setCertificationsInput((product.certifications || []).join(', '))
      setTagsInput((product.tags || []).join(', '))

      // Populate DPP-specific state
      if (product.substancesOfConcern) setSubstancesOfConcern(product.substancesOfConcern)
      if (product.materialComposition) setMaterialComposition(product.materialComposition)
      if (product.supplyChain) setSupplyChain(product.supplyChain)
      if (product.endOfLifeData) setEndOfLife(product.endOfLifeData)
      if (product.sparePartsCatalog) setSparePartsCatalog(product.sparePartsCatalog)
      if (product.batteryPassport) setBatteryPassport(product.batteryPassport)
      if (product.dppPassportFields) setDppPassportData(product.dppPassportFields)
    }
  }, [product])

  // Handle master product selection
  const handleMasterProductSelect = (mpId: string) => {
    setSelectedMasterProductId(mpId)
    if (mpId) {
      const mp = masterProducts.find(m => m.id === mpId)
      if (mp) {
        setFormData(prev => ({
          ...prev,
          name: prev.name || '',
          category: mp.category,
          subcategory: mp.subcategory || prev.subcategory,
          model: mp.model || prev.model,
          gtin: mp.gtin || prev.gtin,
          manufacturer: mp.manufacturer || prev.manufacturer,
          manufacturerCountry: mp.manufacturerCountry || prev.manufacturerCountry,
          materials: mp.materials || prev.materials,
          certifications: mp.certifications || prev.certifications,
          carbonFootprint: mp.carbonFootprint ?? prev.carbonFootprint,
          recyclabilityScore: mp.recyclabilityScore ?? prev.recyclabilityScore,
          expectedLifespan: mp.expectedLifespan ?? prev.expectedLifespan,
          repairabilityScore: mp.repairabilityScore ?? prev.repairabilityScore,
          description: mp.description || prev.description,
          masterProductId: mp.id,
        }))
        // Handle materials - could be string[] or MaterialComposition[]
        const mpMaterialsArray = mp.materials || []
        const mpMaterialsStrings = mpMaterialsArray.map((m: string | { name: string }) =>
          typeof m === 'string' ? m : m.name
        )
        setMaterialsInput(mpMaterialsStrings.join(', '))
        setCertificationsInput((mp.certifications || []).join(', '))

        if (mp.substancesOfConcern) setSubstancesOfConcern(mp.substancesOfConcern)
        if (mp.materialComposition) setMaterialComposition(mp.materialComposition)
        if (mp.endOfLifeData) setEndOfLife(mp.endOfLifeData)
        if (mp.sparePartsCatalog) setSparePartsCatalog(mp.sparePartsCatalog)
      }
    } else {
      setFormData(prev => ({ ...prev, masterProductId: undefined }))
    }
  }

  const handleChange = (field: keyof ProductInput, value: string | number | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleArrayChange = (
    field: 'materials' | 'certifications' | 'tags',
    value: string,
    setter: (v: string) => void
  ) => {
    setter(value)
    const arr = value.split(',').map(s => s.trim()).filter(Boolean)
    setFormData(prev => ({ ...prev, [field]: arr }))
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!formData.name?.trim()) {
      newErrors.name = 'Name is required'
    }
    if (!formData.category) {
      newErrors.category = 'Category is required'
    }
    if (!formData.status) {
      newErrors.status = 'Status is required'
    }
    if (formData.recyclabilityScore !== undefined && (formData.recyclabilityScore < 0 || formData.recyclabilityScore > 100)) {
      newErrors.recyclabilityScore = 'Must be between 0 and 100'
    }
    if (formData.repairabilityScore !== undefined && (formData.repairabilityScore < 0 || formData.repairabilityScore > 10)) {
      newErrors.repairabilityScore = 'Must be between 0 and 10'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    const cleanData: ProductInput = {
      ...formData,
      installationDate: formData.installationDate || undefined,
      warrantyEndDate: formData.warrantyEndDate || undefined,
      carbonFootprint: formData.carbonFootprint || undefined,
      recyclabilityScore: formData.recyclabilityScore || undefined,
      expectedLifespan: formData.expectedLifespan || undefined,
      repairabilityScore: formData.repairabilityScore || undefined,
    }

    setIsLoading(true)
    try {
      const url = isEditing ? `${base}/assets/${productId}` : `${base}/assets`
      const method = isEditing ? 'PATCH' : 'POST'

      const res = await fetch(url, {
        method,
        headers: defaultHeaders,
        credentials: 'include',
        body: JSON.stringify(cleanData),
      })

      if (!res.ok) throw new Error(`Failed to ${isEditing ? 'update' : 'create'} product`)
      onComplete()
    } catch {
      setErrors({ submit: `Failed to ${isEditing ? 'update' : 'create'} product. Please try again.` })
    } finally {
      setIsLoading(false)
    }
  }

  const tabs: { id: TabId; label: string; show?: boolean }[] = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'location', label: 'Location' },
    { id: 'manufacturer', label: 'Materials' },
    { id: 'sustainability', label: 'Sustainability' },
    { id: 'dppPassport', label: 'Digital Passport' },
    { id: 'substances', label: 'Substances' },
    { id: 'supply', label: 'Supply Chain' },
    { id: 'endoflife', label: 'End of Life' },
    { id: 'battery', label: 'Battery', show: hasBattery },
    { id: 'notes', label: 'Notes' },
  ]

  const visibleTabs = tabs.filter(t => t.show !== false)

  const inputClass = (field: string) =>
    `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-esf-navy/20 ${
      errors[field] ? 'border-red-500' : 'border-gray-300'
    }`

  if (isFetching) {
    return (
      <div className="container-page">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3 text-gray-500">
            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Loading...
          </div>
        </div>
      </div>
    )
  }

  if (errors.fetch) {
    return (
      <div className="container-page">
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <p className="text-red-600">{errors.fetch}</p>
          <button onClick={onCancel} className="btn btn-secondary">
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="container-page">
      {/* Page Header */}
      <div className="mb-6">
        <button
          onClick={onCancel}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Products
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Edit Product' : 'Add Product'}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {isEditing
            ? 'Update product details and Digital Product Passport data'
            : 'Create a new product with Digital Product Passport information'}
        </p>
      </div>

      {/* Error Banner */}
      {errors.submit && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {errors.submit}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          {/* DPP Template Selector (only for new products) */}
          {!isEditing && activeMasterProducts.length > 0 && (
            <div className="border-b border-gray-200">
              {/* Header - always visible */}
              <button
                type="button"
                onClick={() => setTemplateSelectorExpanded(!templateSelectorExpanded)}
                className="w-full p-4 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">
                      {selectedMasterProductId
                        ? `Using template: ${activeMasterProducts.find(mp => mp.id === selectedMasterProductId)?.name}`
                        : 'Start from a DPP Template'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {selectedMasterProductId
                        ? 'Click to change or remove template'
                        : 'Pre-fill fields with product passport data (optional)'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {selectedMasterProductId && (
                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                      Template selected
                    </span>
                  )}
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${templateSelectorExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {/* Expanded content */}
              {templateSelectorExpanded && (
                <div className="p-4 bg-gray-50 space-y-4">
                  {/* Search input */}
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search templates by name, category, or manufacturer..."
                      value={templateSearch}
                      onChange={e => setTemplateSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                    {templateSearch && (
                      <button
                        type="button"
                        onClick={() => setTemplateSearch('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* No template option */}
                  <button
                    type="button"
                    onClick={() => {
                      handleMasterProductSelect('')
                      setTemplateSelectorExpanded(false)
                    }}
                    className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                      !selectedMasterProductId
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        !selectedMasterProductId ? 'border-blue-500' : 'border-gray-300'
                      }`}>
                        {!selectedMasterProductId && (
                          <div className="w-2 h-2 rounded-full bg-blue-500" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">Create standalone product</div>
                        <div className="text-sm text-gray-500">Start with a blank form - no template data</div>
                      </div>
                    </div>
                  </button>

                  {/* Template cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-80 overflow-y-auto">
                    {filteredTemplates.map(mp => (
                      <button
                        key={mp.id}
                        type="button"
                        onClick={() => {
                          handleMasterProductSelect(mp.id)
                          setTemplateSelectorExpanded(false)
                        }}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          selectedMasterProductId === mp.id
                            ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/20'
                            : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                            selectedMasterProductId === mp.id ? 'border-blue-500' : 'border-gray-300'
                          }`}>
                            {selectedMasterProductId === mp.id && (
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">{mp.name}</div>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                {ProductCategoryLabels[mp.category as keyof typeof ProductCategoryLabels]}
                              </span>
                              {mp.model && (
                                <span className="text-xs text-gray-500">{mp.model}</span>
                              )}
                            </div>
                            {mp.manufacturer && (
                              <div className="text-sm text-gray-500 mt-1 truncate">
                                {mp.manufacturer}
                              </div>
                            )}
                            {(mp.certifications?.length ?? 0) > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {mp.certifications?.slice(0, 3).map((cert, i) => (
                                  <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-50 text-green-700">
                                    {cert}
                                  </span>
                                ))}
                                {(mp.certifications?.length ?? 0) > 3 && (
                                  <span className="text-xs text-gray-400">+{(mp.certifications?.length ?? 0) - 3} more</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* No results */}
                  {filteredTemplates.length === 0 && templateSearch && (
                    <div className="text-center py-6 text-gray-500">
                      <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p>No templates match "{templateSearch}"</p>
                      <button
                        type="button"
                        onClick={() => setTemplateSearch('')}
                        className="text-blue-600 hover:underline mt-1"
                      >
                        Clear search
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Show inherited from badge when editing a product with DPP template */}
          {product?.masterProductId && (
            <div className="p-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
              <span className="text-sm text-gray-600">Based on DPP template:</span>
              <span className="badge bg-esf-navy/10 text-esf-navy font-mono text-xs">
                {product.masterProductId}
              </span>
            </div>
          )}

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px overflow-x-auto">
              {visibleTabs.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-esf-navy text-esf-navy'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Basic Info Tab */}
            {activeTab === 'basic' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => handleChange('name', e.target.value)}
                    className={inputClass('name')}
                    placeholder="Enter product name"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={e => handleChange('category', e.target.value)}
                    className={inputClass('category')}
                  >
                    {ProductCategories.map(cat => (
                      <option key={cat} value={cat}>
                        {ProductCategoryLabels[cat]}
                      </option>
                    ))}
                  </select>
                  {errors.category && <p className="mt-1 text-sm text-red-500">{errors.category}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory</label>
                  <input
                    type="text"
                    value={formData.subcategory || ''}
                    onChange={e => handleChange('subcategory', e.target.value)}
                    className={inputClass('subcategory')}
                    placeholder="e.g., Bench, Bus Shelter"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                  <input
                    type="text"
                    value={formData.model || ''}
                    onChange={e => handleChange('model', e.target.value)}
                    className={inputClass('model')}
                    placeholder="Model number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
                  <input
                    type="text"
                    value={formData.serialNumber || ''}
                    onChange={e => handleChange('serialNumber', e.target.value)}
                    className={inputClass('serialNumber')}
                    placeholder="Serial number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GTIN</label>
                  <input
                    type="text"
                    value={formData.gtin || ''}
                    onChange={e => handleChange('gtin', e.target.value)}
                    className={inputClass('gtin')}
                    placeholder="Global Trade Item Number (14 digits)"
                  />
                  <p className="mt-1 text-xs text-gray-500">GS1 GTIN for ESPR compliance and Digital Link QR codes</p>
                </div>
              </div>
            )}

            {/* Location & Status Tab */}
            {activeTab === 'location' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.status}
                    onChange={e => handleChange('status', e.target.value)}
                    className={inputClass('status')}
                  >
                    {ProductStatuses.map(s => (
                      <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                  {errors.status && <p className="mt-1 text-sm text-red-500">{errors.status}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                  <select
                    value={formData.condition || ''}
                    onChange={e => handleChange('condition', e.target.value || undefined)}
                    className={inputClass('condition')}
                  >
                    <option value="">Not assessed</option>
                    {ConditionRatings.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Installation Site</label>
                  <input
                    type="text"
                    value={formData.installationSite || ''}
                    onChange={e => handleChange('installationSite', e.target.value)}
                    className={inputClass('installationSite')}
                    placeholder="Site name or address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Installation Date</label>
                  <input
                    type="date"
                    value={formData.installationDate || ''}
                    onChange={e => handleChange('installationDate', e.target.value)}
                    className={inputClass('installationDate')}
                  />
                </div>
              </div>
            )}

            {/* Manufacturer & Materials Tab */}
            {activeTab === 'manufacturer' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</label>
                    <input
                      type="text"
                      value={formData.manufacturer || ''}
                      onChange={e => handleChange('manufacturer', e.target.value)}
                      className={inputClass('manufacturer')}
                      placeholder="Manufacturer name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country of Origin</label>
                    <input
                      type="text"
                      value={formData.manufacturerCountry || ''}
                      onChange={e => handleChange('manufacturerCountry', e.target.value)}
                      className={inputClass('manufacturerCountry')}
                      placeholder="e.g., United Kingdom"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Materials (simple list)</label>
                    <input
                      type="text"
                      value={materialsInput}
                      onChange={e => handleArrayChange('materials', e.target.value, setMaterialsInput)}
                      className={inputClass('materials')}
                      placeholder="Steel, Recycled plastic, Oak (comma-separated)"
                    />
                    <p className="mt-1 text-xs text-gray-500">For detailed material composition, use the Sustainability tab</p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Certifications</label>
                    <input
                      type="text"
                      value={certificationsInput}
                      onChange={e => handleArrayChange('certifications', e.target.value, setCertificationsInput)}
                      className={inputClass('certifications')}
                      placeholder="ISO 14001, CE Mark (comma-separated)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Warranty End Date</label>
                    <input
                      type="date"
                      value={formData.warrantyEndDate || ''}
                      onChange={e => handleChange('warrantyEndDate', e.target.value)}
                      className={inputClass('warrantyEndDate')}
                    />
                  </div>
                </div>

                {/* Material Composition Form */}
                <div className="pt-6 border-t border-gray-200">
                  <MaterialCompositionForm
                    composition={materialComposition}
                    onChange={setMaterialComposition}
                    disabled={isLoading}
                  />
                </div>
              </div>
            )}

            {/* Sustainability Tab */}
            {activeTab === 'sustainability' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Carbon Footprint (kg CO2e)</label>
                  <input
                    type="number"
                    value={formData.carbonFootprint ?? ''}
                    onChange={e => handleChange('carbonFootprint', e.target.value ? parseFloat(e.target.value) : undefined)}
                    className={inputClass('carbonFootprint')}
                    placeholder="e.g., 150"
                    min="0"
                    step="0.1"
                  />
                  <p className="mt-1 text-xs text-gray-500">Total lifecycle carbon footprint</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expected Lifespan (years)</label>
                  <input
                    type="number"
                    value={formData.expectedLifespan ?? ''}
                    onChange={e => handleChange('expectedLifespan', e.target.value ? parseInt(e.target.value) : undefined)}
                    className={inputClass('expectedLifespan')}
                    placeholder="e.g., 25"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recyclability Score (0-100)
                  </label>
                  <input
                    type="number"
                    value={formData.recyclabilityScore ?? ''}
                    onChange={e => handleChange('recyclabilityScore', e.target.value ? parseInt(e.target.value) : undefined)}
                    className={inputClass('recyclabilityScore')}
                    placeholder="0-100"
                    min="0"
                    max="100"
                  />
                  {errors.recyclabilityScore && <p className="mt-1 text-sm text-red-500">{errors.recyclabilityScore}</p>}
                  <p className="mt-1 text-xs text-gray-500">Percentage of product that can be recycled</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Repairability Score (0-10)
                  </label>
                  <input
                    type="number"
                    value={formData.repairabilityScore ?? ''}
                    onChange={e => handleChange('repairabilityScore', e.target.value ? parseFloat(e.target.value) : undefined)}
                    className={inputClass('repairabilityScore')}
                    placeholder="0-10"
                    min="0"
                    max="10"
                    step="0.1"
                  />
                  {errors.repairabilityScore && <p className="mt-1 text-sm text-red-500">{errors.repairabilityScore}</p>}
                  <p className="mt-1 text-xs text-gray-500">EU Repairability Index</p>
                </div>

                {/* ESPR Compliance Indicator */}
                <div className="lg:col-span-4 bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <h4 className="text-sm font-semibold text-blue-800 mb-2">ESPR Compliance Status</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="flex items-center gap-2">
                      {formData.carbonFootprint !== undefined ? (
                        <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
                        </svg>
                      )}
                      <span className="text-sm text-gray-700">Carbon Footprint</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {formData.recyclabilityScore !== undefined ? (
                        <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
                        </svg>
                      )}
                      <span className="text-sm text-gray-700">Recyclability</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {formData.repairabilityScore !== undefined ? (
                        <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
                        </svg>
                      )}
                      <span className="text-sm text-gray-700">Repairability</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Digital Passport Tab */}
            {activeTab === 'dppPassport' && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-800 mb-1">EU Digital Product Passport (DPP)</h4>
                  <p className="text-xs text-blue-700">
                    Complete these fields for full EU ESPR (2024/1781) compliance. Fields cover identification, materials, sustainability, and lifecycle information.
                  </p>
                </div>
                <DppPassportForm
                  data={dppPassportData}
                  onChange={setDppPassportData}
                  disabled={isLoading}
                />
              </div>
            )}

            {/* Substances of Concern Tab */}
            {activeTab === 'substances' && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-blue-800 mb-1">ESPR Article 7 Compliance</h4>
                  <p className="text-sm text-blue-700">
                    Document any substances of concern (SVHC, hazardous substances) as required by the EU Ecodesign for Sustainable Products Regulation.
                  </p>
                </div>
                <SubstancesOfConcernForm
                  substances={substancesOfConcern}
                  onChange={setSubstancesOfConcern}
                  disabled={isLoading}
                />
              </div>
            )}

            {/* Supply Chain Tab */}
            {activeTab === 'supply' && (
              <SupplyChainForm
                supplyChain={supplyChain}
                onChange={setSupplyChain}
                disabled={isLoading}
              />
            )}

            {/* End of Life Tab */}
            {activeTab === 'endoflife' && (
              <div className="space-y-8">
                <div>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
                    <h4 className="font-medium text-blue-800 mb-1">End of Life Instructions</h4>
                    <p className="text-sm text-blue-700">
                      Provide disassembly and recycling instructions as required by ESPR for product circularity.
                    </p>
                  </div>
                  <EndOfLifeForm endOfLife={endOfLife} onChange={setEndOfLife} disabled={isLoading} />
                </div>

                <div className="border-t pt-8">
                  <div className="bg-green-50 border border-green-100 rounded-lg p-4 mb-6">
                    <h4 className="font-medium text-green-800 mb-1">Spare Parts Catalog (ESPR Article 11)</h4>
                    <p className="text-sm text-green-700">
                      List available spare parts for repair and maintenance to extend product lifespan.
                    </p>
                  </div>
                  <SparePartsForm catalog={sparePartsCatalog} onChange={setSparePartsCatalog} disabled={isLoading} />
                </div>
              </div>
            )}

            {/* Battery Passport Tab (conditional) */}
            {activeTab === 'battery' && hasBattery && (
              <BatteryPassportForm
                battery={batteryPassport}
                onChange={setBatteryPassport}
                disabled={isLoading}
              />
            )}

            {/* Notes & Tags Tab */}
            {activeTab === 'notes' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description || ''}
                    onChange={e => handleChange('description', e.target.value)}
                    rows={8}
                    className={inputClass('description')}
                    placeholder="Product description..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formData.notes || ''}
                    onChange={e => handleChange('notes', e.target.value)}
                    rows={8}
                    className={inputClass('notes')}
                    placeholder="Internal notes..."
                  />
                </div>

                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                  <input
                    type="text"
                    value={tagsInput}
                    onChange={e => handleArrayChange('tags', e.target.value, setTagsInput)}
                    className={inputClass('tags')}
                    placeholder="outdoor, accessible, premium (comma-separated)"
                  />
                  {(formData.tags?.length || 0) > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {formData.tags?.map((tag, i) => (
                        <span key={i} className="badge bg-esf-navy/10 text-esf-navy">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
            <button type="button" onClick={onCancel} className="btn btn-secondary" disabled={isLoading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </>
              ) : isEditing ? 'Update Product' : 'Create Product'}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
